"use client";

import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PageCareerSchema } from "@/sanity/pages/PageCareerTemplate";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

export default function CompetitionSection({ data }: { data: PageCareerSchema }) {
  const [activeCard, setActiveCard] = useState(-1);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      const scrollOffset = isMobile ? window.innerHeight / 2.5 : window.innerHeight / 2;
      const scrollPosition = window.scrollY + scrollOffset;
      let newActiveCard = -1;

      cardRefs.current.forEach((ref, index) => {
        if (ref) {
          const { top } = ref.getBoundingClientRect();
          const absoluteTop = top + window.scrollY;

          if (scrollPosition >= absoluteTop) {
            newActiveCard = index;
          }
        }
      });

      setActiveCard(newActiveCard);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <section className="relative">
      <div className="flex relative flex-col lg:pt-0 lg:bg-transparent bg-[#F0F4F2] pt-[7rem] lg:gap-[4rem] w-[min(94.375rem,100%)] mx-auto">
        <div className="container flex relative z-10 flex-col lg:items-center px-[2rem] lg:px-[2.25rem]">
          {data?.competitionChip && (
            <p className="font-medium font-instrument text-black/70 text-[2.25rem] tracking-[0.025rem] mb-[1rem]">
              {data.competitionChip}
            </p>
          )}
          {data?.competitionTitle && (
            <div className="flex flex-col items-center richtext richtext-start rose-headings:mb-[2.5rem] lg:richtext-center lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] prose-strong:text-black prose-strong:font-[400] prose-headings:text-[5rem] lg:prose-headings:text-[7rem] lg:prose-headings:mb-[1.5rem]">
              <PortableText
                value={data.competitionTitle}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="text-[2rem] font-geist leading-[140%] text-[#1C1F22]/80 mb-0 tracking-[-0.045rem] capitalize w-[min(41.5rem,100%)]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            </div>
          )}
        </div>

        {data?.competitionCards && (
          <div className="flex flex-col items-center gap-[2.62rem] px-[2rem] pt-[2rem] rounded-[2rem] bg-[#F0F4F2] pb-[6rem] lg:pt-[4rem] lg:pb-[5rem] lg:px-[5.25rem]">
            <h3 className="text-[3rem] text-[#103037] lg:text-center font-[500] leading-[150%] tracking-[-0.1rem] font-geist mb-0">
              {data.competitionCardsTitle}
            </h3>

            <div className="relative flex-1 w-full">
              {/* Progress Line */}
              <div className="absolute top-[50%] -translate-y-1/2 h-[78%] lg:h-[80%] w-[1rem] left-[1rem] overflow-clip">
                <div className="relative w-full h-[calc(100%-0.8rem)] pt-[1rem]">
                  <div className="absolute top-0 left-0 h-full rounded-full w-[0.1rem] bg-black/10" />
                  <div className="sticky top-[82%] before:content-[''] before:h-[100vh] before:w-[0.1rem] before:bg-[#6DA991] before:rounded-full before:absolute before:left-0 before:bottom-0" />
                </div>
              </div>

              {/* Cards */}
              <div className="flex flex-col w-full gap-[2.62rem]">
                {data.competitionCards.map((item, index) => {
                  const isActive = index <= activeCard;

                  return (
                    <motion.div
                      key={index}
                      ref={(el) => {
                        cardRefs.current[index] = el;
                      }}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{
                        duration: 0.6,
                        delay: index * 0.1,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className="flex relative z-10 items-center gap-[2rem]">
                      {/* Number Badge */}
                      <div
                        className={`flex-shrink-0 w-[2rem] h-[2rem] rounded-full flex items-center justify-center text-[1.75rem] font-geist font-[500] transition-all duration-500 bg-[#F0F4F2] border border-[#6DA991] ${
                          isActive
                            ? "bg-[#6DA991] p-[.38rem] border border-[#6DA991]/50"
                            : "bg-[#F0F4F2] border border-[#6DA991]"
                        }`}>
                        <div
                          className={`w-[1rem] aspect-square rounded-full p-[.38rem] ${
                            isActive ? "bg-[#6DA991]" : "bg-[#F0F4F2]"
                          }`}
                        />
                      </div>

                      {/* Card Content */}
                      <div
                        className={`flex-1 flex flex-row gap-[2rem] p-[1.5rem] rounded-[1.125rem] bg-white transition-all duration-500 ${
                          isActive ? "opacity-100" : "opacity-60"
                        }`}>
                        <p className="flex flex-col flex-shrink-0 justify-center items-center self-stretch mb-0 border text-[2.58rem] text-[#393D41] font-[500] font-geist border-[#755630]/10 w-[6.89413rem] p-[.86rem] rounded-[.86rem]">
                          0{index + 1}
                        </p>
                        <div className="flex flex-col flex-1">
                          <p
                            className={`text-[2.5rem] font-[500] font-geist mb-0 transition-colors duration-500 ${
                              isActive ? "text-[#393D41]" : "text-[#393D41]/60"
                            }`}>
                            {item.title}
                          </p>
                          <p
                            className={`text-[2.25rem] lg:text-[2rem] font-geist leading-[150%] mb-0 transition-colors duration-500 ${
                              isActive ? "text-[#393D41]/70" : "text-[#393D41]/40"
                            }`}>
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
